/**
 * Enhanced API Logging Utility
 * 
 * Provides comprehensive logging for all API calls to make debugging easier.
 * Logs: firing, fired, result, errors, timing, and request details.
 */

export interface ApiLogEntry {
  requestId: string;
  method: string;
  url: string;
  timestamp: string;
  phase: 'firing' | 'fired' | 'result' | 'error';
  duration?: number;
  status?: number;
  data?: any;
  error?: any;
  headers?: Record<string, string>;
  params?: any;
  body?: any;
}

class ApiLogger {
  private logs: ApiLogEntry[] = [];
  private maxLogs: number = 1000;
  private isEnabled: boolean = true;

  constructor() {
    // Logging disabled by default - only enable with explicit flags for debugging
    // To enable: localStorage.setItem('enableApiLogging', 'true') or add ?debug=true to URL
    this.isEnabled =
      (typeof localStorage !== 'undefined' && localStorage.getItem('enableApiLogging') === 'true') ||
      (typeof window !== 'undefined' && window.location.search.includes('debug=true'));
  }

  /**
   * Generate a unique request ID for tracking
   */
  private generateRequestId(): string {
    return `api_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current timestamp in readable format
   */
  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Clean sensitive data from logs
   */
  private sanitizeData(data: any): any {
    if (!data) return data;
    
    const sensitiveKeys = ['password', 'token', 'authorization', 'secret', 'key'];
    
    if (typeof data === 'object') {
      const sanitized = { ...data };
      for (const key of Object.keys(sanitized)) {
        if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
          sanitized[key] = '[REDACTED]';
        }
      }
      return sanitized;
    }
    
    return data;
  }

  /**
   * Add log entry
   */
  private addLog(entry: ApiLogEntry): void {
    if (!this.isEnabled) return;

    this.logs.push(entry);
    
    // Keep only the last maxLogs entries to prevent memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Log API request firing
   */
  logFiring(method: string, url: string, config?: any): string {
    const requestId = this.generateRequestId();
    const timestamp = this.getTimestamp();

    const logEntry: ApiLogEntry = {
      requestId,
      method: method.toUpperCase(),
      url,
      timestamp,
      phase: 'firing',
      headers: this.sanitizeData(config?.headers),
      params: this.sanitizeData(config?.params),
      body: this.sanitizeData(config?.data)
    };

    this.addLog(logEntry);

    // Console logging only when enabled
    if (!this.isEnabled) return requestId;

    // Console logging with emoji and colors
    console.group(`🚀 API FIRING: ${method.toUpperCase()} ${url}`);
    console.log(`📋 Request ID: ${requestId}`);
    console.log(`⏰ Timestamp: ${timestamp}`);
    console.log(`🔧 Method: ${method.toUpperCase()}`);
    console.log(`🌐 URL: ${url}`);

    if (config?.headers && Object.keys(config.headers).length > 0) {
      console.log(`📤 Headers:`, this.sanitizeData(config.headers));
    }

    if (config?.params && Object.keys(config.params).length > 0) {
      console.log(`🔍 Params:`, this.sanitizeData(config.params));
    }

    if (config?.data) {
      console.log(`📦 Body:`, this.sanitizeData(config.data));
    }

    console.groupEnd();

    return requestId;
  }

  /**
   * Log API request fired (sent)
   */
  logFired(requestId: string, method: string, url: string): void {
    const timestamp = this.getTimestamp();

    const logEntry: ApiLogEntry = {
      requestId,
      method: method.toUpperCase(),
      url,
      timestamp,
      phase: 'fired'
    };

    this.addLog(logEntry);

    if (!this.isEnabled) return;
    console.log(`🎯 API FIRED: ${method.toUpperCase()} ${url} [${requestId}] at ${timestamp}`);
  }

  /**
   * Log API response result
   */
  logResult(requestId: string, method: string, url: string, response: any, startTime: number): void {
    const timestamp = this.getTimestamp();
    const duration = performance.now() - startTime;

    const logEntry: ApiLogEntry = {
      requestId,
      method: method.toUpperCase(),
      url,
      timestamp,
      phase: 'result',
      duration,
      status: response?.status,
      data: this.sanitizeData(response?.data)
    };

    this.addLog(logEntry);

    if (!this.isEnabled) return;

    // Console logging with success styling
    console.group(`✅ API SUCCESS: ${method.toUpperCase()} ${url}`);
    console.log(`📋 Request ID: ${requestId}`);
    console.log(`⏱️  Duration: ${duration.toFixed(2)}ms`);
    console.log(`📊 Status: ${response?.status || 'N/A'}`);
    console.log(`⏰ Completed: ${timestamp}`);

    if (response?.data) {
      console.log(`📥 Response:`, this.sanitizeData(response.data));
    }

    // Performance warnings
    if (duration > 2000) {
      console.warn(`⚠️  SLOW REQUEST: ${duration.toFixed(2)}ms (>2s)`);
    } else if (duration > 1000) {
      console.warn(`⚠️  MODERATE DELAY: ${duration.toFixed(2)}ms (>1s)`);
    }

    console.groupEnd();
  }

  /**
   * Log API error
   */
  logError(requestId: string, method: string, url: string, error: any, startTime: number): void {
    const timestamp = this.getTimestamp();
    const duration = performance.now() - startTime;

    const logEntry: ApiLogEntry = {
      requestId,
      method: method.toUpperCase(),
      url,
      timestamp,
      phase: 'error',
      duration,
      status: error?.response?.status,
      error: {
        message: error?.message,
        code: error?.code,
        response: this.sanitizeData(error?.response?.data)
      }
    };

    this.addLog(logEntry);

    if (!this.isEnabled) return;

    // Console logging with error styling
    console.group(`❌ API ERROR: ${method.toUpperCase()} ${url}`);
    console.log(`📋 Request ID: ${requestId}`);
    console.log(`⏱️  Duration: ${duration.toFixed(2)}ms`);
    console.log(`📊 Status: ${error?.response?.status || 'Network Error'}`);
    console.log(`⏰ Failed: ${timestamp}`);
    console.error(`💥 Error:`, error?.message || 'Unknown error');

    if (error?.response?.data) {
      console.error(`📥 Error Response:`, this.sanitizeData(error.response.data));
    }

    if (error?.stack) {
      console.error(`📚 Stack:`, error.stack);
    }

    console.groupEnd();
  }

  /**
   * Get all logs
   */
  getLogs(): ApiLogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs for a specific request
   */
  getRequestLogs(requestId: string): ApiLogEntry[] {
    return this.logs.filter(log => log.requestId === requestId);
  }

  /**
   * Get recent logs (last N entries)
   */
  getRecentLogs(count: number = 50): ApiLogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Get logs by URL pattern
   */
  getLogsByUrl(urlPattern: string): ApiLogEntry[] {
    return this.logs.filter(log => log.url.includes(urlPattern));
  }

  /**
   * Get error logs only
   */
  getErrorLogs(): ApiLogEntry[] {
    return this.logs.filter(log => log.phase === 'error');
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats(): {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    averageResponseTime: number;
    slowRequests: number;
    errorRate: number;
  } {
    const resultLogs = this.logs.filter(log => log.phase === 'result');
    const errorLogs = this.logs.filter(log => log.phase === 'error');
    const totalRequests = resultLogs.length + errorLogs.length;
    
    const durations = [...resultLogs, ...errorLogs]
      .filter(log => log.duration !== undefined)
      .map(log => log.duration!);
    
    const averageResponseTime = durations.length > 0 
      ? durations.reduce((sum, duration) => sum + duration, 0) / durations.length 
      : 0;
    
    const slowRequests = durations.filter(duration => duration > 1000).length;
    
    return {
      totalRequests,
      successfulRequests: resultLogs.length,
      failedRequests: errorLogs.length,
      averageResponseTime,
      slowRequests,
      errorRate: totalRequests > 0 ? (errorLogs.length / totalRequests) * 100 : 0
    };
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    console.log('🧹 API logs cleared');
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    localStorage.setItem('enableApiLogging', enabled.toString());
    console.log(`📝 API logging ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if logging is enabled
   */
  isLoggingEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Print summary of recent API activity
   */
  printSummary(count: number = 20): void {
    const recentLogs = this.getRecentLogs(count);
    const stats = this.getPerformanceStats();
    
    console.group('📊 API ACTIVITY SUMMARY');
    console.log(`📈 Performance Stats:`, stats);
    console.log(`📋 Recent ${count} API calls:`);
    
    recentLogs.forEach(log => {
      const emoji = log.phase === 'error' ? '❌' : 
                   log.phase === 'result' ? '✅' : 
                   log.phase === 'fired' ? '🎯' : '🚀';
      
      const duration = log.duration ? ` (${log.duration.toFixed(2)}ms)` : '';
      const status = log.status ? ` [${log.status}]` : '';
      
      console.log(`${emoji} ${log.method} ${log.url}${status}${duration} - ${log.timestamp}`);
    });
    
    console.groupEnd();
  }
}

// Create and export singleton instance
export const apiLogger = new ApiLogger();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).apiLogger = apiLogger;
}

// Export utility functions for easy access
export const logApiFiring = (method: string, url: string, config?: any) => 
  apiLogger.logFiring(method, url, config);

export const logApiFired = (requestId: string, method: string, url: string) => 
  apiLogger.logFired(requestId, method, url);

export const logApiResult = (requestId: string, method: string, url: string, response: any, startTime: number) => 
  apiLogger.logResult(requestId, method, url, response, startTime);

export const logApiError = (requestId: string, method: string, url: string, error: any, startTime: number) => 
  apiLogger.logError(requestId, method, url, error, startTime);